import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TruckIcon,
  GlobeAltIcon,
  ClockIcon,
  ShieldCheckIcon,
  CubeIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  BoltIcon,
  MapPinIcon,
  CheckCircleIcon,
  StarIcon,
  ArrowRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

function Services() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('international');

  const handleNavigation = (path) => {
    navigate(path);
  };

  const serviceCategories = [
    {
      id: 'international',
      title: 'International Shipping',
      icon: GlobeAltIcon,
      description: 'Global shipping solutions'
    },
    {
      id: 'business',
      title: 'Business Solutions',
      icon: BuildingOfficeIcon,
      description: 'Enterprise logistics services'
    }
  ];

  const services = [
    {
      category: 'international',
      title: "International Express",
      description: "Fast international shipping to over 190 countries with customs handling included.",
      features: [
        "2-5 business days worldwide",
        "Customs clearance handled",
        "Full tracking & insurance",
        "Express documentation",
        "Duty & tax calculation"
      ],
      price: "From $49.99",
      icon: GlobeAltIcon,
      color: "bg-orange-500",
      popular: false
    },
    {
      category: 'international',
      title: "International Standard",
      description: "Reliable international shipping at competitive rates for businesses and individuals.",
      features: [
        "5-10 business days",
        "Customs assistance",
        "Package tracking",
        "Insurance options",
        "Competitive rates"
      ],
      price: "From $34.99",
      icon: GlobeAltIcon,
      color: "bg-purple-600",
      popular: false
    },
    {
      category: 'business',
      title: "Bulk Shipping",
      description: "Volume discounts and dedicated service for businesses shipping multiple packages.",
      features: [
        "Volume discounts available",
        "Dedicated account manager",
        "Scheduled pickups",
        "Monthly invoicing",
        "Custom reporting"
      ],
      price: "Custom pricing",
      icon: BuildingOfficeIcon,
      color: "bg-indigo-600",
      popular: false
    },
    {
      category: 'business',
      title: "Warehouse Solutions",
      description: "Complete warehousing and fulfillment services in major cities worldwide.",
      features: [
        "Storage facilities",
        "Pick & pack services",
        "Inventory management",
        "Multi-city distribution",
        "Fulfillment solutions"
      ],
      price: "Contact for quote",
      icon: BuildingOfficeIcon,
      color: "bg-teal-600",
      popular: false
    },
    {
      category: 'business',
      title: "Supply Chain Management",
      description: "End-to-end logistics solutions for businesses with complex supply chain needs.",
      features: [
        "Full supply chain optimization",
        "Custom integrations",
        "SLA guarantees",
        "Advanced analytics",
        "24/7 support"
      ],
      price: "Custom pricing",
      icon: ChartBarIcon,
      color: "bg-pink-600",
      popular: false
    }
  ];

  const stats = [
    { number: "150K+", label: "Packages Delivered", icon: CubeIcon },
    { number: "50+", label: "Countries Served", icon: MapPinIcon },
    { number: "98.2%", label: "On-Time Delivery", icon: ClockIcon },
    { number: "24/7", label: "Support Available", icon: PhoneIcon }
  ];

  const coverageAreas = [
    {
      province: "North America",
      cities: ["New York", "Los Angeles", "Toronto", "Chicago", "Miami", "Vancouver"],
      icon: "🏙️"
    },
    {
      province: "Europe",
      cities: ["London", "Paris", "Berlin", "Amsterdam", "Madrid", "Rome"],
      icon: "🏔️"
    },
    {
      province: "Asia Pacific",
      cities: ["Tokyo", "Singapore", "Sydney", "Hong Kong", "Seoul", "Bangkok"],
      icon: "🌊"
    },
    {
      province: "Middle East",
      cities: ["Dubai", "Abu Dhabi", "Riyadh", "Doha"],
      icon: "🌅"
    },
    {
      province: "Africa",
      cities: ["Johannesburg", "Cairo", "Lagos", "Nairobi"],
      icon: "🌾"
    },
    {
      province: "South America",
      cities: ["São Paulo", "Buenos Aires", "Mexico City", "Lima"],
      icon: "🏞️"
    }
  ];

  const testimonials = [
    {
      name: "Michael Chen",
      role: "Operations Manager",
      company: "Global Retail Co.",
      location: "New York, USA",
      text: "CargoHaul has transformed our delivery operations. Their express service is reliable and affordable.",
      rating: 5
    },
    {
      name: "Emma Thompson",
      role: "Import Manager",
      company: "London Imports Ltd",
      location: "London, UK",
      text: "Their international shipping service is exceptional. We've expanded our reach across continents with their reliable network.",
      rating: 5
    },
    {
      name: "James Wilson",
      role: "CEO",
      company: "Sydney Distribution",
      location: "Sydney, Australia",
      text: "Best logistics partner we've worked with. The bulk shipping discounts have saved us thousands in shipping costs.",
      rating: 5
    }
  ];

  const filteredServices = services.filter(service => service.category === activeTab);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-blue-700">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center mix-blend-overlay opacity-20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-6">
              Our Services
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto mb-12 leading-relaxed">
              Comprehensive logistics solutions for businesses and individuals worldwide. 
              From local deliveries to international trade, we've got you covered.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 rounded-xl mb-3">
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl font-black text-white mb-1">{stat.number}</div>
                  <div className="text-white/80 text-sm font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-16 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {serviceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                className={`flex items-center gap-3 px-8 py-4 rounded-xl font-bold text-lg transition-all ${
                  activeTab === category.id
                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                }`}
              >
                <category.icon className={`w-6 h-6 ${activeTab === category.id ? 'text-white' : 'text-blue-600'}`} />
                <span>{category.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              {serviceCategories.find(c => c.id === activeTab)?.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {serviceCategories.find(c => c.id === activeTab)?.description}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl p-8 border-2 transition-all duration-300 hover:shadow-2xl ${
                  service.popular
                    ? 'border-blue-500 ring-4 ring-blue-100 shadow-xl'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                {service.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className={`inline-flex items-center justify-center w-16 h-16 ${service.color} rounded-2xl mb-6`}>
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                <div className="pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-3xl font-black text-gray-900">{service.price}</div>
                      {service.price !== "Custom pricing" && service.price !== "Contact for quote" && (
                        <div className="text-sm text-gray-500">per shipment</div>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => handleNavigation('/new-shipment')}
                    className={`w-full py-3 rounded-xl font-bold transition-all ${
                      service.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Get Started
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              Global Coverage Areas
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We serve major cities and regions across all continents worldwide
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coverageAreas.map((area, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-3xl">{area.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900">{area.province}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {area.cities.map((city, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium"
                    >
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">Don't see your city? We're expanding our coverage daily.</p>
            <button
              onClick={() => handleNavigation('/contact')}
              className="px-8 py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl"
            >
              Request Coverage in Your Area
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              Trusted by Businesses Worldwide
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See what our customers have to say about their experience
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border-2 border-gray-100 hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-gray-700 mb-6 italic leading-relaxed text-lg">
                  "{testimonial.text}"
                </blockquote>
                <div className="pt-6 border-t border-gray-200">
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                  <div className="text-sm text-gray-500">{testimonial.company}, {testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
            Ready to Ship with CargoHaul?
            </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Join thousands of satisfied customers worldwide. Get started today and experience 
            the difference with our reliable, fast, and affordable shipping solutions.
            </p>
            
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-xl border border-white/20">
              <PhoneIcon className="w-5 h-5 text-white" />
              <span className="font-semibold text-white">+27 74 258 6662</span>
              </div>
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-xl border border-white/20">
              <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
              <span className="font-semibold text-white">+27 62 367 6161 (WhatsApp)</span>
              </div>
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-xl border border-white/20">
              <EnvelopeIcon className="w-5 h-5 text-white" />
              <span className="font-semibold text-white">support@cargohaulco.org</span>
            </div>
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-xl border border-white/20">
              <ClockIcon className="w-5 h-5 text-white" />
              <span className="font-semibold text-white">24/7 Support</span>
              </div>
            </div>
            
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
              onClick={() => handleNavigation('/new-shipment')}
              className="px-10 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all transform hover:scale-105 shadow-xl"
              >
              Create Your First Shipment
              </button>
              <button
              onClick={() => handleNavigation('/contact')}
              className="px-10 py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white rounded-xl font-bold text-lg hover:bg-white/20 transition-all"
              >
              Get a Quote
              </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Services;
